import {
  AfterContentChecked, afterNextRender, afterRender,
  AfterViewInit,
  Component, ElementRef,
  EventEmitter, Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {SynapiItem} from "../../../models/Synapi/SynapiItem";
import {SynapiListItems} from "../../../models/Synapi/SynapiListItems";
import {SynapiService} from "../../../services/synapi.service";
import {SynapiPredicate} from "../../../models/Synapi/SynapiPredicate";
import {SynapiItemComponent} from "../synapi-item/synapi-item.component";
import {SwiperComponent, SwiperModule} from "swiper/angular";
import SwiperCore, {FreeMode, Lazy, Mousewheel, Pagination, Scrollbar, SwiperOptions} from 'swiper';
import {isPlatformBrowser, NgClass, NgForOf, NgIf} from "@angular/common";
import {ThemeService} from "../../../services/theme.service";
import {debounceTime, fromEvent, Observable} from "rxjs";
import {InfiniteScrollModule} from "ngx-infinite-scroll";
import {set} from "ol/transform";
import {HorizontalScrollDirective} from "../../../directives/horizontal-scroll.directive";
SwiperCore.use([Mousewheel,Pagination,Scrollbar,FreeMode,Lazy]);
@Component({
  selector: 'synapi-list-items',
  templateUrl: './synapi-list-items.component.html',
  standalone: true,
  imports: [
    SynapiItemComponent,
    SwiperModule,
    NgForOf,
    NgIf,
    NgClass,
    InfiniteScrollModule,
    HorizontalScrollDirective
  ],
  host: {ngSkipHydration: 'true'},
  styleUrls: ['./synapi-list-items.component.scss']
})

export class SynapiListItemsComponent implements OnInit,OnDestroy, OnChanges, AfterViewInit {
  public loading: boolean = true;
  public page: number = 1;
  public pagecount: number = 0;
  @Input() size: number = 9;
  @Input() pagecountMax?: number;
  @Input() col: number = 5;
  @Input() standalone: boolean = false;
  @Input() overflow: boolean = true;
  private listItems: SynapiListItems;
  @Input() predicate: SynapiPredicate;
  @Input() ids: string[]; // ids d'items
  @Input() id: string; // id de sélection
  @Input() sort?: "ids"|"default"; // id de sélection
  @Input() scids: string[]; // ids de sélection
  @Input() datemin: string;
  @Input() datemax: string;
  @Input() scrollHorizontal: boolean;
  @Output() onPredicateChange = new EventEmitter<SynapiPredicate>();
  @Output() onListChange = new EventEmitter<boolean>();
  @Output() onTotalChange = new EventEmitter<number>();

  public displayedItems: SynapiItem[][] = [];
  @ViewChild('container') containerRef: ElementRef | undefined;

  constructor(public synapi: SynapiService, private themeService : ThemeService, @Inject(PLATFORM_ID) private platformId: Object) {
  }

  ngOnInit() {
    this.reset();
  }

  ngAfterViewInit() {
    if(isPlatformBrowser(this.platformId)){
      setTimeout(async () => {
        this.setContainerHeight();
        await this.onListScroll();
      },1000)
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.standalone) {
      if (changes['ids']?.currentValue != changes['ids']?.previousValue) {
        await this.updateListItemsQuery();
      }
      if (changes['id']?.currentValue != changes['id']?.previousValue) {
        await this.updateListItemsQuery();
      }
      if (changes['scids']?.currentValue != changes['scids']?.previousValue) {
        await this.updateListItemsQuery();
      }
      if (changes['pagecountMax']?.currentValue != changes['pagecountMax']?.previousValue) {
        if(this.listItems){
          this.reset();
          await this.updateListItemsQuery();
        }
      }
    }
  }

  ngOnDestroy(){
    this.reset();
  }

  public reset(){
    this.page = 1;
    this.pagecount = this.pagecountMax ?? this.pagecount;
    this.displayedItems = [];
    this.displayedItems[this.page] = [];
    this.resetListScrollPosition();
  }

  public async updateListItemsQuery() {
    const predicate = new SynapiPredicate();
    if(this.datemin){
      predicate.datemin = this.datemin;
    }
    if(this.datemax){
      predicate.datemax = this.datemax;
    }
    if(this.scids && this.scids.length>0){
      predicate.scids = this.scids;
    }
    if(this.ids && this.ids.length>0){
      predicate.ids = this.ids;
    }
    if(this.id){
      predicate.id = this.id;
    }
    if(this.sort){
      predicate.sort = this.sort;
    }
    predicate.size = this.themeService.isMobile() ? 3 : this.size;
    this.predicate = predicate;
    this.loading = true;
    this.synapi.getItems(this.predicate).then((listItems)=>{
      this.updateListItems(listItems);
    })
  }

  public updateListItems(listItems: SynapiListItems){
    this.listItems = listItems;
    if(this.listItems.items){
      this.pagecount = this.listItems.pagecount<=30 ? this.listItems.pagecount :30;
      this.pagecount = this.pagecountMax ?? this.pagecount;
      this.displayedItems[this.page] = this.listItems.items;
    }
    this.onTotalChange.emit(this.listItems.total);
    this.onListChange.emit(true);
    this.loading = false;
  }

  async loadListChunk() {
    this.page++;
    if(this.predicate){
      this.predicate.page = this.page;
    }
    if(this.page <= this.pagecount){
      if (this.standalone && !this.loading) {
        this.loading = true;
        this.synapi.getItems(this.predicate).then((listItems)=>{
          this.updateListItems(listItems);
        })
      } else {
        this.onPredicateChange.emit(this.predicate);
      }
    }
  }

  async onListScroll() {
    if (!this.loading) {
      await this.loadListChunk();
    }
  }

  resetListScrollPosition() {
    if (this.containerRef && this.containerRef.nativeElement) {
      this.containerRef.nativeElement.scrollTop = 0; // Reset scroll position
    }
  }

  public setContainerHeight(){
    if(!this.scrollHorizontal){
      if(this.containerRef?.nativeElement && this.containerRef?.nativeElement){
        const heightScrollable = this.containerRef.nativeElement.clientHeight;
        if(heightScrollable>0){
          this.containerRef.nativeElement.style.height = heightScrollable+'px';
          this.containerRef.nativeElement.firstChild.style.height = heightScrollable+20+'px';
        }
      }
    }
  }

  protected readonly Array = Array;
}
